import { MessageOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  ChatsByPhoneAndDate,
  MessageResolutionStatus,
  TrackingNumberStatus,
  TrackingNumberWithAgents,
} from '../../../../interfaces/appointments';
import { AuthService } from '../../../../services';
import { SimplifiedMember } from '../ChatDrawer/ChatDrawer';

interface ChatHeaderProps {
  members: SimplifiedMember[];
  selectedMember: SimplifiedMember | null;
  isSearchingMembers: boolean;
  smsOptedOut: boolean;
  canEditMember: boolean;
  trackingNumbers: TrackingNumberWithAgents[];
  currentTrackingNumber: string;
  loading: boolean;
  initialMessages: ChatsByPhoneAndDate;
  onMemberChange: (member: SimplifiedMember | null) => void;
  onSearch: (term: string) => void;
  onTabChange: (event: React.SyntheticEvent, value: string) => void;
  onClose: () => void;
}

export const ChatHeader = ({
  members,
  selectedMember,
  isSearchingMembers,
  smsOptedOut,
  canEditMember,
  trackingNumbers,
  currentTrackingNumber,
  loading,
  initialMessages,
  onMemberChange,
  onSearch,
  onTabChange,
  onClose,
}: ChatHeaderProps) => {
  const { user_id: coreProviderId } = AuthService.getAuth() || {};

  return (
    <Box className='chat-header'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Box display='flex' width='100%'>
          <Autocomplete
            disabled={loading || smsOptedOut || !canEditMember}
            className='member-selector'
            options={members}
            filterOptions={(x) => x}
            onChange={(e, v) => onMemberChange(v)}
            noOptionsText='No members found'
            value={selectedMember}
            getOptionLabel={(option) => `${option.name} ID:${option.id}`}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant='outlined'
                placeholder='Search a member'
                onChange={(e) => onSearch(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Box sx={{ marginRight: '33px', marginTop: '4px' }}>
                      {isSearchingMembers && <CircularProgress color='inherit' size={14} />}
                      {params.InputProps.endAdornment}
                    </Box>
                  ),
                  startAdornment: (
                    <>
                      <Avatar sx={{ color: 'action.active', mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component='li' {...props}>
                <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                  {`${option.name?.[0]}${option.name?.[1]}`.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography fontSize='0.8rem'>{option.name}</Typography>
                  <Typography variant='caption' color='textSecondary'>
                    ID:{option.id} Phone:{option.phone}
                  </Typography>
                </Box>
              </Box>
            )}
          />
          {smsOptedOut && (
            <Chip
              label='SMS opted-out'
              sx={{ marginLeft: '15px', color: 'red', backgroundColor: '#FF000026' }}
            />
          )}
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={currentTrackingNumber}
        onChange={onTabChange}
        variant='scrollable'
        scrollButtons='auto'
        className='chat-tab'
        sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          key='all-messages'
          label={
            <Box id='chat-tab-info'>
              <p>All</p>
              <MessageOutlined />
            </Box>
          }
          value='all'
          disabled={loading}
          className={`all-tab ${currentTrackingNumber === 'all' ? 'chat-active-tab' : ''}`}
        />
        {trackingNumbers.map(({ id, trackingNumber, status, label, agents }) => {
          const messages = initialMessages[trackingNumber] || {};
          const messageValues = Object.values(messages);

          const hasAMessage = messageValues.length > 0;
          const hasPendingMessages = messageValues.some((msgArray) =>
            msgArray.some((msg) => msg.resolutionStatus === MessageResolutionStatus.Pending),
          );

          const isSystemNumber = !agents || agents.length === 0;
          const isAttachedToCurrentProvider = (agents || []).some(
            (agent) => agent.coreProviderId === coreProviderId,
          );

          const statusColor =
            messageValues.length === 0 ? 'gray' : hasPendingMessages ? '#FF7B00' : '#389FBA';

          // We skip numbers that are inactive or not attached to the current provider
          const shouldSeeNumber =
            hasAMessage ||
            (status !== TrackingNumberStatus.INACTIVE &&
              (isAttachedToCurrentProvider || isSystemNumber));

          if (!shouldSeeNumber) {
            return null;
          }

          return (
            <Tab
              key={id}
              label={
                <Box id='chat-tab-info'>
                  <Box id='chat-tab-status' sx={{ backgroundColor: statusColor }} />
                  <p id='chat-tab-label'>{label}</p>
                  <p id='chat-tab-phone'>{trackingNumber}</p>
                </Box>
              }
              value={trackingNumber}
              disabled={loading}
              className={currentTrackingNumber === trackingNumber ? 'chat-active-tab' : undefined}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};
